import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton,
  Chip, Box, Tooltip, TablePagination,
  Typography, Divider
} from "@mui/material";
import { DeleteOutline, EditOutlined, Add } from "@mui/icons-material";
import APIURL from "../api/apiClient";
import SubCategoryModal from "./SubCategory";
import { message } from "antd";
import Loader from "./Loader";

const CategoryTable = ({ refreshTrigger }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const result = await APIURL.get("/fetchcategories.php");
      const categoriesArray = Array.isArray(result) ? result : JSON.parse(result);

      // Fetch subcategories for each category
      const categoriesWithSubs = await Promise.all(
        categoriesArray.map(async (cat) => {
          const res = await APIURL.get(`/subCategories.php?category_id=${cat.id}`);
          return { ...cat, subcategories: res.data || [] };
        })
      );

      setCategories(categoriesWithSubs);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // CategoryTable.jsx
  useEffect(() => {
    fetchCategories();
  }, [refreshTrigger]); // 👈 added refreshTrigger


  const handleDeleteMainCategory = async (categoryId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this category?");
    if (!confirmDelete) return;

    try {
      let response = await APIURL.delete(`/fetchcategories.php?id=${categoryId}`);
      console.log(response);

      // If response is a string (not JSON), try to parse it
      let parsedResponse = response;
      if (typeof response === "string") {
        try {
          parsedResponse = JSON.parse(response.trim());
        } catch {
          // fallback if not JSON
          parsedResponse = { status: false, message: response };
        }
      }

      if (parsedResponse.status === true || parsedResponse.status === "true") {
        message.success(parsedResponse.message);
        fetchCategories();
      } else {
        message.error(parsedResponse.message);
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("Something went wrong");
    }
  };



  const handleAddSubcategory = async (data) => {
    try {
      const response = await APIURL.post("/subCategories.php", data);
      if (response.status) {
        message.success(response.message);
        setOpenModal(false);
        fetchCategories();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("Something went wrong");
    }
  };


  const handleDeleteSubcategory = async (subId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this subcategory?");
    if (!confirmDelete) return;

    try {
      const response = await APIURL.delete(`/subCategories.php?id=${subId}`);
      if (response.status) {
        message.success(response.message);
        fetchCategories();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("Something went wrong");
    }
  };


  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ width: "100%", overflowX: "auto", p: 3, borderRadius: 2, boxShadow: 3 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f3f3f3" }}>
              <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              // Show loader when data is being fetched
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box sx={{ py: 4 }}>
                    <Loader type="table" loading={true} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              // Show this only after loading finishes and no records found
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No categories to display
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              // Normal category + subcategory rendering
              categories
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((cat, index) => (
                  <React.Fragment key={cat.id}>
                    {/* Main category row */}
                    <TableRow hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{cat.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={cat.status === 1 ? "Active" : "Inactive"}
                          color={cat.status === 1 ? "success" : "default"}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                        <Tooltip title="Delete Category">
                          <IconButton
                            sx={{ color: "red" }}
                            onClick={() => handleDeleteMainCategory(cat.id)}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Subcategories row */}
                    <TableRow>
                      <TableCell colSpan={4} sx={{ background: "#fafafa", py: 1.5 }}>
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                          {cat.subcategories.length > 0 ? (
                            cat.subcategories.map((sub) => (
                              <Chip
                                key={sub.id}
                                label={sub.name}
                                onDelete={() => handleDeleteSubcategory(sub.id)}
                                color="primary"
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 1.5 }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No subcategories
                            </Typography>
                          )}
                          <Tooltip title="Add Subcategory">
                            <IconButton
                              size="small"
                              sx={{
                                ml: cat.subcategories.length ? 1 : 0,
                                border: "1px dashed #1976d2",
                                color: "#1976d2",
                              }}
                              onClick={() => {
                                setSelectedCategory(cat);
                                setOpenModal(true);
                              }}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Divider between categories */}
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 0 }}>
                        <Divider />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
            )}
          </TableBody>

        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={categories.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 15]}
        sx={{ mt: 2 }}
      />

      {selectedCategory && (
        <SubCategoryModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          categoryId={selectedCategory.id}
          categoryName={selectedCategory.name}
          onSubmit={handleAddSubcategory}
        />
      )}
    </Paper>

  );
};

export default CategoryTable;
